import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { useSubscriptions } from '../hooks/useSubscriptions';
import { useAuth } from '../../../context/AuthContext';
import type { XServicesService, ProfileSubscription } from '../../../types';

type UseSubscriptionsReturn = ReturnType<typeof useSubscriptions>;

interface SubscriptionContextProps extends UseSubscriptionsReturn {
  activeServices: string[];
}

const SubscriptionContext = createContext<SubscriptionContextProps>({
  activeServices: [],
  services: [],
  subscriptions: [],
  plans: {},
  loading: false,
  error: null,
  loadPlansForService: async () => {},
  subscribe: async () => ({}) as ProfileSubscription,
  renew: async () => ({}) as ProfileSubscription,
  cancel: async () => {},
  resume: async () => {},
  getActiveFor: () => undefined,
  renewalDueList: [],
  refresh: async () => {},
});

export function SubscriptionProvider({
  children,
  profileId: propProfileId,
}: {
  children: ReactNode;
  profileId?: number | undefined;
}) {
  const { profileId: authProfileId, isAuthenticated } = useAuth();
  const rawProfileId = propProfileId !== undefined ? propProfileId : authProfileId;
  const profileId = isAuthenticated ? rawProfileId : undefined;
  const subscriptionData = useSubscriptions(profileId);

  const activeServices = useMemo(() => {
    const list = new Set<string>();

    for (const sub of subscriptionData.subscriptions) {
      const isActive =
        sub.subscriptionStatus === 'ACTIVE' ||
        (sub.subscriptionStatus === 'CANCELLED' && (sub.daysRemaining ?? 0) > 0);

      if (isActive) {
        if (sub.serviceCode) {
          list.add(sub.serviceCode.trim().toUpperCase());
        }
        if (sub.serviceId) {
          if (sub.serviceId === 1) list.add('SOCIETY_MGT');
          if (sub.serviceId === 2) list.add('REAL_ESTATE');
          if (sub.serviceId === 3) list.add('MATRIMONIAL');

          const svc = subscriptionData.services.find((s) => s.serviceId === sub.serviceId);
          if (svc?.serviceCode) {
            list.add(svc.serviceCode.trim().toUpperCase());
          }
        }
      }
    }

    return Array.from(list);
  }, [subscriptionData.subscriptions, subscriptionData.services]);

  return (
    <SubscriptionContext.Provider value={{ ...subscriptionData, activeServices }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export const useSubscriptionContext = () => useContext(SubscriptionContext);
